import {
  Fragment,
  ReactChild,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { addToast, Button } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Form, Row, Col } from 'reactstrap';
import {
  CourseInfo,
  COURSE_TYPES,
  CurriculumValues,
  ERRORS_DEFAULT,
  HasErrors,
  LegendInfo,
  LEGEND_DEFAULT,
} from '../../../../types/CurriculumTypes';
import { PeriodType } from '../../../../types/PeriodType';
import { CurriculumErrorModal } from './CurriculumErrorModal';
import CurriculumFormContentTop from './CurriculumFormContentTop';
import { CurriculumPublishModal } from './CurriculumPublishModal';
import CurriculumSemester from './CurriculumSemester';
import { CurriculumSemesterDeleteModal } from './CurriculumSemesterDeleteModal';
import { CourseModal } from './parts/course/CourseModal';
import CourseModalLoader from './parts/course/CourseModalLoader';
import {
  DegreeActivitiesInCurriculumTypeOptions,
  DEGREE_ACTIVITIES_IN_CURRICULUM_TYPE_OPTIONS,
} from './parts/CurriculumDegreeActivities';
import { transformData } from './parts/CurriculumDegreeActivities/dataTransformation';
import CurriculumFormContentBottom from './CurriculumFormContentBottom';

/**
 * @deprecated
 * Always is undefined defaultValues props, because never is sending for parent-component
 */
const getDefaultValues = (defaultValues?: CurriculumValues) =>
  defaultValues
    ? {
        mentionCode: defaultValues.mentionCode,
        mentionName: defaultValues.mentionName,
        totalCredits: defaultValues.totalCredits,
        requiredCredits: defaultValues.requiredCredits,
        optionalCredits: defaultValues.optionalCredits,
        otherCredits: defaultValues.otherCredits,
        groupCredits: defaultValues.groupCredits ?? 0,
        activitiesNumber: defaultValues.activitiesNumber,
        periodType: defaultValues.periodType,
        curriculumActivities: defaultValues.activitiesType
          ? transformData(defaultValues.activitiesType)
          : [],
        hasActivitiesChecked: !defaultValues.activitiesType.length,
        degreeDocuments: defaultValues.degreeDocuments,
        hasDegreeDocumentsChecked: !defaultValues.degreeDocuments?.length,
        degreeActivitiesInCurriculumType:
          defaultValues.degreeActivitiesInCurriculumType,
        degreeActivitiesInCurriculumGroup:
          defaultValues.degreeActivitiesInCurriculumGroup,
        totalDurationSemesters: defaultValues.totalDurationSemesters,
        graduationDurationSemesters: defaultValues.graduationDurationSemesters,
        studyPlanDurationSemesters: defaultValues.studyPlanDurationSemesters,
      }
    : {
        mentionCode: undefined,
        mentionName: undefined,
        totalCredits: undefined,
        requiredCredits: undefined,
        optionalCredits: undefined,
        otherCredits: undefined,
        groupCredits: 0,
        activitiesNumber: undefined,
        periodType: undefined,
        curriculumActivities: [],
        hasActivitiesChecked: false,
        degreeDocuments: [],
        hasDegreeDocumentsChecked: false,
        degreeActivitiesInCurriculumType: undefined,
        degreeActivitiesInCurriculumGroup: undefined,
        totalDurationSemesters: undefined,
        graduationDurationSemesters: undefined,
        studyPlanDurationSemesters: undefined,
      };

export interface CurriculumFormProps {
  curriculumData: any;
  periodType?: PeriodType;
  defaultValues?: CurriculumValues;
  setDisabledEditButton: (value: boolean) => void;
  children: (props: {
    isSubmitting: boolean;
    isDirty?: boolean;
    legend: any;
  }) => ReactChild;
  studyPlanName: string;
  readOnly: boolean;
  isMention: boolean;
  prefix?: string;
  showSubmitButton?: boolean;
  disabledSubmitButton?: boolean;
}

const CurriculumForm = (props: CurriculumFormProps) => {
  const {
    prefix,
    defaultValues,
    children,
    curriculumData,
    readOnly,
    setDisabledEditButton,
    isMention,
    periodType,
    showSubmitButton,
    disabledSubmitButton,
  } = props;

  const { versionId } = useParams<{ id?: string; versionId?: string }>();
  const { t } = useTranslation();

  let optionsSelectSemesters = new Array(20).fill({});

  optionsSelectSemesters = optionsSelectSemesters.map((e: any, index: any) => ({
    label: index === 0 ? `${index + 1} Periodo` : `${index + 1} Periodos`,
    value: `SEM_${index + 1}`,
    key: index + 1,
  }));

  const [semesterCount, setSemesterCount] = useState<number>(1);
  const [currentSemester, setCurrentSemester] = useState<number>(0);
  const [showCourseModal, setShowCourseModal] = useState<boolean>(false);
  const [
    showCurriculumErrorModal,
    setShowCurriculumErrorModal,
  ] = useState<boolean>(false);
  const [
    showCurriculumPublishModal,
    setShowCurriculumPublishModal,
  ] = useState<boolean>(false);
  const [
    showCurriculumSemesterDeleteModal,
    setShowCurriculumSemesterDeleteModal,
  ] = useState<boolean>(false);
  const [coursesList, setCoursesList] = useState<Array<CourseInfo>>([]);
  const [curriculumFormattedData, setCurriculumFormattedData] = useState<any>(
    {},
  );
  const [errors, setErrors] = useState<HasErrors>(ERRORS_DEFAULT);
  const [legend, setLegend] = useState<LegendInfo>(LEGEND_DEFAULT);

  const methods = useForm<CurriculumValues>({
    mode: 'onSubmit',
    defaultValues: getDefaultValues(defaultValues),
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    watch,
    setValue,
    setError,
  } = methods;

  const [
    mentionCodeValue,
    mentionNameValue,
    semestersValue,
    totalCreditsValue,
    requiredCreditsValue,
    optionalCreditsValue,
    otherCreditsValue,
    activitiesNumberValue,
    degreeActivitiesInCurriculumGroup,
    degreeActivitiesInCurriculumType,
    curriculumActivities,
  ] = watch([
    'mentionCode',
    'mentionName',
    'semestersAmount',
    'totalCredits',
    'requiredCredits',
    'optionalCredits',
    'otherCredits',
    'activitiesNumber',
    'degreeActivitiesInCurriculumGroup',
    'degreeActivitiesInCurriculumType',
    'curriculumActivities',
  ]);

  const addCourse = useCallback(
    (course: CourseInfo): boolean => {
      // Course-group items count toward `required` (Obligatorios), since
      // the dedicated `groupCredits` bucket was retired.
      if (course.isCourseGroup) {
        setLegend({
          ...legend,
          total: legend.total + parseInt(course.credits.toString()),
          totalNeeded: totalCreditsValue,
          required: legend.required + parseInt(course.credits.toString()),
          requiredNeeded: requiredCreditsValue,
        });
        setCoursesList([...coursesList, course]);
        return true;
      }

      if (course.type === COURSE_TYPES.OBLIGATORIO.alias) {
        const search = coursesList.find((row) => row.code === course.code);
        if (!search) {
          setLegend({
            ...legend,
            total: legend.total + parseInt(course.credits.toString()),
            totalNeeded: totalCreditsValue,
            required: legend.required + parseInt(course.credits.toString()),
            requiredNeeded: requiredCreditsValue,
          });
          setCoursesList([...coursesList, course]);
          return true;
        } else {
          return false;
        }
      } else {
        if (course.type.includes('Optativo'))
          setLegend({
            ...legend,
            total: legend.total + parseInt(course.credits.toString()),
            totalNeeded: totalCreditsValue,
            optional: legend.optional + parseInt(course.credits.toString()),
            optionalNeeded: optionalCreditsValue,
          });
        if (course.type === COURSE_TYPES.OTROS.alias)
          setLegend({
            ...legend,
            total: legend.total + parseInt(course.credits.toString()),
            totalNeeded: totalCreditsValue,
            other: legend.other + parseInt(course.credits.toString()),
            otherNeeded: otherCreditsValue,
          });
        if (course.type === COURSE_TYPES.ACTIVIDAD.alias)
          setLegend({
            ...legend,
            activities: legend.activities + 1,
            activitiesNeeded: activitiesNumberValue,
          });
        setCoursesList([...coursesList, course]);
        return true;
      }
    },
    [
      legend,
      setLegend,
      coursesList,
      setCoursesList,
      totalCreditsValue,
      requiredCreditsValue,
      optionalCreditsValue,
      otherCreditsValue,
      activitiesNumberValue,
    ],
  );

  const validateCourses = useCallback(
    (formValues) => {
      const semestersArray = new Array(semesterCount).fill(0);
      const foundedErrors: any = {};
      coursesList.forEach((course) => {
        semestersArray[course.semester - 1] += 1;
      });

      // HU 15576 — totalCredits is the only number the admin still types,
      // so it's the only one that can disagree with the running totals
      // panel. The other five fields are auto-synced from `legend` and
      // RHF excludes them (disabled inputs) from formValues, so reading
      // them here would crash. Their validation is tautological under
      // auto-sync, so we drop the checks.
      if (
        legend.total !== parseInt((formValues.totalCredits ?? 0).toString())
      ) {
        foundedErrors.has = true;
        foundedErrors.total = true;
      }
      semestersArray.forEach((semester) => {
        if (semester === 0) {
          foundedErrors.has = true;
          foundedErrors.semester = true;
        }
      });
      if (
        !formValues.hasActivitiesChecked &&
        formValues.curriculumActivities.length === 0
      ) {
        foundedErrors.has = true;
        foundedErrors.activitiesChecked = true;
      }

      if (
        !formValues.hasDegreeDocumentsChecked &&
        !!formValues.degreeDocuments?.length === false
      ) {
        foundedErrors.has = true;
        foundedErrors.degreeDocumentsChecked = true;
      }
      setErrors({
        ...errors,
        ...foundedErrors,
      });

      return {
        ...errors,
        ...foundedErrors,
      };
    },
    [errors, setErrors, coursesList, legend, semesterCount],
  );

  const extraCourses = useMemo(() => {
    if (
      degreeActivitiesInCurriculumType?.value ===
        DegreeActivitiesInCurriculumTypeOptions.Grouped &&
      degreeActivitiesInCurriculumGroup &&
      curriculumActivities?.length
    ) {
      return [
        {
          name: degreeActivitiesInCurriculumGroup,
        },
      ];
    } else if (
      degreeActivitiesInCurriculumType?.value ===
      DegreeActivitiesInCurriculumTypeOptions.Normal
    ) {
      return curriculumActivities?.map((c) => ({ name: c.name }));
    } else {
      return undefined;
    }
  }, [
    curriculumActivities,
    degreeActivitiesInCurriculumGroup,
    degreeActivitiesInCurriculumType,
  ]);

  const printSemesters = (count: number) => {
    let semesters = [];
    for (let i = 0; i < count; i++) {
      semesters.push(
        <CurriculumSemester
          key={i}
          id={i + 1}
          periodTypeName={periodType?.name}
          setCurrentSemester={setCurrentSemester}
          setShowCourseModal={setShowCourseModal}
          semesterCoursesList={coursesList.filter(
            (course) => course.semester === i + 1,
          )}
          setCoursesList={setCoursesList}
          coursesList={coursesList}
          readOnly={readOnly}
          legend={legend}
          setLegend={setLegend}
          setDisabledEditButton={setDisabledEditButton}
          extraCourses={count - 1 === i ? extraCourses : undefined}
        />,
      );
    }
    return semesters;
  };

  const formatSemesters = useCallback(() => {
    const semestersObject: any = {};
    coursesList.forEach((course) => {
      if (!semestersObject[course.semester]) {
        semestersObject[course.semester] = {};
        semestersObject[course.semester].semesterId = course.semesterId;
        semestersObject[course.semester].semesterList = [];
        semestersObject[course.semester].semesterGroupList = [];
      }
      // HU 15578 — course-group items go to a separate payload bucket.
      if (course.isCourseGroup) {
        const payload: any = {
          courseGroupId: course.courseGroupId,
          credits: parseInt(course.credits.toString()),
        };
        if (course.semesterCourseGroupId) {
          payload.id = course.semesterCourseGroupId;
        }
        semestersObject[course.semester].semesterGroupList.push(payload);
        return;
      }
      let auxCourse: any = {};
      if (course.semesterCourseId) auxCourse.id = course.semesterCourseId;
      if (course.type === COURSE_TYPES.OBLIGATORIO.alias)
        auxCourse = {
          ...auxCourse,
          courseType: {
            ...COURSE_TYPES.OBLIGATORIO,
          },
          course: {
            id: course.id,
            name: course.name,
            code: course.code,
            shortening: course.shortening,
            credits: parseInt(course.credits.toString()),
          },
        };
      if (course.type === COURSE_TYPES.OPTATIVO_RELIGIOSO.alias)
        auxCourse = {
          ...auxCourse,
          courseType: {
            ...COURSE_TYPES.OPTATIVO_RELIGIOSO,
          },
        };
      if (course.type === COURSE_TYPES.OPTATIVO_DEPORTIVO.alias)
        auxCourse = {
          ...auxCourse,
          courseType: {
            ...COURSE_TYPES.OPTATIVO_DEPORTIVO,
          },
        };
      if (course.type === COURSE_TYPES.OPTATIVO_OTRO.alias)
        auxCourse = {
          ...auxCourse,
          courseType: {
            ...COURSE_TYPES.OPTATIVO_OTRO,
          },
        };
      if (course.type === COURSE_TYPES.OTROS.alias)
        auxCourse = {
          ...auxCourse,
          courseType: {
            ...COURSE_TYPES.OTROS,
          },
        };
      if (course.type === COURSE_TYPES.ACTIVIDAD.alias)
        auxCourse = {
          ...auxCourse,
          courseType: {
            ...COURSE_TYPES.ACTIVIDAD,
          },
          course: {
            id: course.id,
            name: course.name,
            code: course.code,
            shortening: course.shortening,
            credits: 0,
          },
        };
      if (
        course.type !== COURSE_TYPES.ACTIVIDAD.alias &&
        course.type !== COURSE_TYPES.OBLIGATORIO.alias
      )
        auxCourse.optionalSlot = {
          id: course.id,
          name: course.type,
          credits: parseInt(course.credits.toString()),
        };
      semestersObject[course.semester].semesterList.push(auxCourse);
    });

    const semestersArray: Array<any> = [];
    for (let index = 1; index <= semesterCount; index++) {
      let auxCurrentSemester: any = {};
      if (semestersObject[index]?.semesterId !== undefined)
        auxCurrentSemester.id = semestersObject[index].semesterId;
      semestersArray.push({
        ...auxCurrentSemester,
        name: `Periodo ${index}`,
        semesterIndex: index,
        semesterCourses: semestersObject[index]?.semesterList ?? [],
        // HU 15578 — course-group items per semester.
        semesterCourseGroups: semestersObject[index]?.semesterGroupList ?? [],
      });
    }

    return semestersArray;
  }, [coursesList, semesterCount]);

  const onSubmit = useCallback(
    async (formValues: CurriculumValues) => {
      let {
        mentionCode,
        mentionName,
        totalCredits,
        requiredCredits,
        optionalCredits,
        otherCredits,
        groupCredits,
        activitiesNumber,
        curriculumActivities,
        autoCalculate,
        graduationPercentage,
        activitiesPercentage = 0,
        degreeDocuments,
        curriculumImage,
        // SIES
        studyPlanDurationSemesters,
        graduationDurationSemesters,
        totalDurationSemesters,
      } = formValues;

      if (autoCalculate) {
        if (curriculumActivities && curriculumActivities.length > 0) {
          // Calculate sum of percentages from all curriculum activities
          const percentageSum = curriculumActivities.reduce(
            (sum, activity) => sum + (Number(activity.percentage) || 0),
            0,
          );
          if (percentageSum !== 100) {
            setError('curriculumActivities', {
              message: t('studyPlans.curriculum.errorOnActivitiesPercentage'),
            });
            return;
          }
        }
        if (
          Number(graduationPercentage) + Number(activitiesPercentage) !==
          100
        ) {
          addToast({
            text: t('studyPlans.curriculum.errorOnGraduationPercentage'),
            color: 'danger',
            icon: 'error',
          });
          return;
        }
      }

      // HU 15576 — RHF excludes disabled inputs from formValues, so the
      // five read-only fields come back as undefined here. Read them
      // straight from `legend` (the running totals panel) — it's the
      // source the auto-sync effect mirrors into the form anyway, and
      // for downstream payload usage we want the canonical values.
      requiredCredits = legend.required ?? 0;
      optionalCredits = legend.optional ?? 0;
      otherCredits = legend.other ?? 0;
      groupCredits = legend.groupCredits ?? 0;
      activitiesNumber = legend.activities ?? 0;

      const toInt = (v: number | string | undefined): number =>
        parseInt((v ?? 0).toString());

      if (
        toInt(totalCredits) !==
        toInt(requiredCredits) +
          toInt(optionalCredits) +
          toInt(otherCredits) +
          toInt(groupCredits)
      ) {
        setShowCurriculumErrorModal(true);
      } else {
        const currentErrors = validateCourses(formValues);
        if (currentErrors.has) {
          setShowCurriculumErrorModal(true);
        } else {
          let mentionData = {};
          if (isMention) {
            mentionData = {
              name: mentionName,
              code: mentionCode,
            };
          } else if (
            curriculumData &&
            curriculumData.name &&
            curriculumData.code
          ) {
            mentionData = {
              name: curriculumData.name,
              code: curriculumData.code,
            };
          }
          const degreeActivitiesInCurriculumType =
            formValues?.degreeActivitiesInCurriculumType?.value ?? '';
          const data = {
            ...mentionData,
            status: 'borrador',
            isPrimary: !isMention,
            duration: parseInt(semesterCount.toString()),
            totalCredits: parseInt(totalCredits.toString()),
            obligatoryCredits: parseInt(requiredCredits.toString()),
            optionalCredits: parseInt(optionalCredits.toString()),
            otherCredits: parseInt(otherCredits.toString()),
            activityNumber: parseInt(activitiesNumber.toString()),
            groupCredits: parseInt(groupCredits.toString()),
            studyPlanVersion: {
              id: versionId,
            },
            semesters: formatSemesters(),
            curriculumActivities: curriculumActivities,
            graduationPercentageForDegreeAverage: autoCalculate
              ? Number(graduationPercentage)
              : null,
            degreeActivitiesInCurriculumType,
            degreeActivitiesInCurriculumGroup:
              degreeActivitiesInCurriculumType ===
              DegreeActivitiesInCurriculumTypeOptions.Grouped
                ? formValues.degreeActivitiesInCurriculumGroup
                : '',
            degreeDocuments,
            imageId: curriculumImage ? curriculumImage.id : null,
            studyPlanDurationSemesters,
            graduationDurationSemesters,
            totalDurationSemesters,
          };
          setCurriculumFormattedData(data);
          setShowCurriculumPublishModal(true);
        }
      }
    },
    [
      formatSemesters,
      isMention,
      semesterCount,
      versionId,
      validateCourses,
      curriculumData,
      legend,
      t,
      setError,
    ],
  );

  const setDegreeActivitiesInCurriculumType = useCallback(
    (degreeActivitiesInCurriculumType?: string | null | undefined) => {
      setValue(
        'degreeActivitiesInCurriculumType',
        DEGREE_ACTIVITIES_IN_CURRICULUM_TYPE_OPTIONS.find(
          (item) =>
            item.value === degreeActivitiesInCurriculumType ??
            DegreeActivitiesInCurriculumTypeOptions.None,
        ) || DEGREE_ACTIVITIES_IN_CURRICULUM_TYPE_OPTIONS[0],
      );
    },
    [setValue],
  );

  const setPeriodTypeInputToValue = useCallback(
    (type?: PeriodType) => {
      if (type) {
        setValue('periodType', {
          label: type?.name,
          value: type?.id,
        });
      }
    },
    [setValue],
  );

  const setSemesterInputToValue = useCallback(
    (index: number) => {
      setValue(
        'semestersAmount',
        optionsSelectSemesters.find((item: any) => item.key === index) || null,
      );
    },
    [setValue, optionsSelectSemesters],
  );

  useEffect(() => {
    setValue('mentionCode', curriculumData?.code || '');
    setValue('mentionName', curriculumData?.name || '');
    setSemesterInputToValue(curriculumData?.duration);
    setValue('totalCredits', curriculumData?.totalCredits || '0');
    setValue('requiredCredits', curriculumData?.obligatoryCredits || '0');
    setValue('optionalCredits', curriculumData?.optionalCredits || '0');
    setValue('otherCredits', curriculumData?.otherCredits || '0');
    setValue('groupCredits', curriculumData?.groupCredits || '0');
    setValue('activitiesNumber', curriculumData?.activityNumber || '0');
    setValue('curriculumImage', curriculumData?.image || null);
    setValue(
      'studyPlanDurationSemesters',
      curriculumData?.curriculumSies?.studyPlanDurationSemesters || null,
    );
    setValue(
      'graduationDurationSemesters',
      curriculumData?.curriculumSies?.graduationDurationSemesters || null,
    );
    setValue(
      'totalDurationSemesters',
      curriculumData?.curriculumSies?.totalDurationSemesters || null,
    );

    setSemesterCount(curriculumData?.duration);
    setPeriodTypeInputToValue(periodType);
    setDegreeActivitiesInCurriculumType(
      curriculumData?.degreeActivitiesInCurriculumType,
    );
    setValue(
      'degreeActivitiesInCurriculumGroup',
      curriculumData?.degreeActivitiesInCurriculumGroup || '',
    );
    const auxCourses: CourseInfo[] = [];
    let auxLegend: any = {
      ...LEGEND_DEFAULT,
    };
    curriculumData?.semesters.forEach((semester: any) => {
      // HU 15578 — hydrate course-group items into the same coursesList
      // shape used by regular courses, flagged with isCourseGroup.
      semester.semesterCourseGroups?.forEach((groupRow: any) => {
        const credits = parseInt(groupRow.credits ?? 0);
        auxCourses.push({
          id: undefined,
          type: t('studyPlans.curriculum.courseModal.courseGroup'),
          name: groupRow.courseGroup?.name ?? '',
          code: groupRow.courseGroup?.code ?? '',
          credits,
          shortening: groupRow.courseGroup?.code ?? '',
          semester: semester.semesterIndex,
          semesterId: semester.id,
          semesterCourseId: undefined,
          isCourseGroup: true,
          courseGroupId: groupRow.courseGroupId,
          courseGroupSchool: groupRow.courseGroup?.school?.name ?? null,
          semesterCourseGroupId: groupRow.id,
        });
        auxLegend = {
          ...auxLegend,
          total: auxLegend.total + credits,
          totalNeeded: totalCreditsValue,
          required: auxLegend.required + credits,
          requiredNeeded: requiredCreditsValue,
        };
      });
      semester.semesterCourses.forEach((courseData: any) => {
        let auxType = '';
        for (const [, courseType] of Object.entries(COURSE_TYPES)) {
          if (courseData.courseType.id === courseType.id)
            auxType = courseType.alias;
        }
        auxCourses.push({
          id: courseData.course?.id || courseData.optionalSlot?.id || undefined,
          type: auxType,
          name: courseData.course?.name || courseData.optionalSlot?.name || '',
          code: courseData.course?.code || '',
          credits:
            courseData.course?.credits || courseData.optionalSlot?.credits || 0,
          shortening: courseData.course?.shortening || '',
          semester: semester.semesterIndex,
          semesterId: semester.id,
          semesterCourseId: courseData.id,
        });

        if (auxType === COURSE_TYPES.OBLIGATORIO.alias) {
          auxLegend = {
            ...auxLegend,
            total: auxLegend.total + parseInt(courseData.course?.credits),
            totalNeeded: totalCreditsValue,
            required: auxLegend.required + parseInt(courseData.course?.credits),
            requiredNeeded: requiredCreditsValue,
          };
        } else if (auxType.includes('Optativo')) {
          auxLegend = {
            ...auxLegend,
            total:
              auxLegend.total + parseInt(courseData.optionalSlot?.credits || 0),
            totalNeeded: totalCreditsValue,
            optional:
              auxLegend.optional +
              parseInt(courseData.optionalSlot?.credits || 0),
            optionalNeeded: optionalCreditsValue,
          };
        } else if (auxType === COURSE_TYPES.OTROS.alias) {
          auxLegend = {
            ...auxLegend,
            total:
              auxLegend.total + parseInt(courseData.optionalSlot?.credits || 0),
            totalNeeded: totalCreditsValue,
            other:
              auxLegend.other + parseInt(courseData.optionalSlot?.credits || 0),
            otherNeeded: otherCreditsValue,
          };
        } else if (auxType === COURSE_TYPES.ACTIVIDAD.alias) {
          auxLegend = {
            ...auxLegend,
            activities: auxLegend.activities + 1,
            activitiesNeeded: activitiesNumberValue,
          };
        }
      });
    });
    setCoursesList(auxCourses);
    setLegend(auxLegend);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculumData, periodType]);

  useEffect(() => {
    if (
      semestersValue &&
      semestersValue.key < semesterCount &&
      coursesList.findIndex(
        (course) => course.semester > semestersValue.key,
      ) !== -1
    ) {
      setShowCurriculumSemesterDeleteModal(true);
    } else {
      setSemesterCount(semestersValue?.key || 1);
    }
  }, [semestersValue, coursesList, semesterCount]);

  useEffect(() => {
    setLegend({
      ...legend,
      totalNeeded: totalCreditsValue || legend.totalNeeded,
      requiredNeeded: requiredCreditsValue || legend.requiredNeeded,
      optionalNeeded: optionalCreditsValue || legend.optionalNeeded,
      otherNeeded: otherCreditsValue || legend.otherNeeded,
      activitiesNeeded: activitiesNumberValue || legend.activitiesNeeded,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalCreditsValue,
    requiredCreditsValue,
    optionalCreditsValue,
    otherCreditsValue,
    activitiesNumberValue,
  ]);

  // HU 15576 — mirror legend running totals into the form fields so the
  // admin no longer types these values. totalCredits stays admin-driven.
  // Values are written as strings to match the existing hydration pattern
  // — the @octano TextInput renders numeric 0 as an empty cell, which
  // would break the validateCourses cross-check (parseInt("") = NaN).
  useEffect(() => {
    setValue('requiredCredits', String(legend.required ?? 0) as never);
    setValue('optionalCredits', String(legend.optional ?? 0) as never);
    setValue('otherCredits', String(legend.other ?? 0) as never);
    setValue('activitiesNumber', String(legend.activities ?? 0) as never);
    setValue('groupCredits', String(legend.groupCredits ?? 0) as never);
  }, [
    legend.required,
    legend.optional,
    legend.other,
    legend.activities,
    legend.groupCredits,
    setValue,
  ]);

  useEffect(() => {
    if (
      (curriculumData && curriculumData.code !== mentionCodeValue) ||
      (curriculumData && curriculumData.name !== mentionNameValue) ||
      (curriculumData &&
        totalCreditsValue &&
        curriculumData.totalCredits !==
          parseInt(totalCreditsValue.toString())) ||
      (curriculumData &&
        requiredCreditsValue &&
        curriculumData.obligatoryCredits !==
          parseInt(requiredCreditsValue.toString())) ||
      (curriculumData &&
        optionalCreditsValue &&
        curriculumData.optionalCredits !==
          parseInt(optionalCreditsValue.toString())) ||
      (curriculumData &&
        otherCreditsValue &&
        curriculumData.otherCredits !==
          parseInt(otherCreditsValue.toString())) ||
      (curriculumData &&
        activitiesNumberValue &&
        curriculumData.activityNumber !==
          parseInt(activitiesNumberValue.toString())) ||
      (curriculumData && curriculumData.duration !== semesterCount)
    ) {
      setDisabledEditButton(false);
    }
  }, [
    mentionCodeValue,
    mentionNameValue,
    totalCreditsValue,
    requiredCreditsValue,
    optionalCreditsValue,
    otherCreditsValue,
    activitiesNumberValue,
    semesterCount,
    curriculumData,
    setDisabledEditButton,
  ]);

  return (
    <>
      <Fragment>
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <CurriculumFormContentTop
              hasPeriodType={!!periodType}
              isMention={isMention}
              readOnly={readOnly}
              printSemesters={printSemesters}
              optionsSelectSemesters={optionsSelectSemesters}
              periodTypes={[]}
              semesterCount={semesterCount}
            />
            {children({ isSubmitting, isDirty, legend })}
            <CurriculumFormContentBottom readOnly={readOnly} />
            {showSubmitButton && (
              <Row>
                <Col xs={{ size: 12 }} className="d-flex justify-content-end">
                  <Col xs={{ size: 12 }} md={{ size: 5 }} lg={{ size: 4 }}>
                    <div className="pb-3 pb-md-0 mt-5">
                      <Button
                        type="submit"
                        text={t(`${prefix}.save`)}
                        loading={isSubmitting}
                        disabled={disabledSubmitButton}
                        fullwidth
                      />
                    </div>
                  </Col>
                </Col>
              </Row>
            )}
          </Form>
        </FormProvider>
        <CourseModalLoader>
          <CourseModal
            show={showCourseModal}
            addCourse={addCourse}
            semester={currentSemester}
            onClose={useCallback(() => {
              setShowCourseModal(false);
            }, [setShowCourseModal])}
            setDisabledEditButton={setDisabledEditButton}
          />
        </CourseModalLoader>
        <CurriculumErrorModal
          show={showCurriculumErrorModal}
          semesterError={errors.has || false}
          errors={errors}
          legend={legend}
          onClose={useCallback(() => {
            setShowCurriculumErrorModal(false);
            setErrors(ERRORS_DEFAULT);
          }, [setShowCurriculumErrorModal, setErrors])}
        />
        <CurriculumPublishModal
          show={showCurriculumPublishModal}
          curriculumData={curriculumFormattedData}
          onClose={useCallback(() => {
            setShowCurriculumPublishModal(false);
            setErrors(ERRORS_DEFAULT);
          }, [setShowCurriculumPublishModal, setErrors])}
        />
        <CurriculumSemesterDeleteModal
          show={showCurriculumSemesterDeleteModal}
          semesterIndex={semestersValue?.key}
          setCoursesList={setCoursesList}
          coursesList={coursesList}
          semesterCount={semesterCount}
          setSemesterCount={setSemesterCount}
          setSemesterInputToValue={setSemesterInputToValue}
          legend={legend}
          setLegend={setLegend}
          onClose={useCallback(() => {
            setShowCurriculumSemesterDeleteModal(false);
          }, [setShowCurriculumSemesterDeleteModal])}
        />
      </Fragment>
    </>
  );
};

export default CurriculumForm;
